import React, { useEffect, useState } from 'react';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
  IconButton,
  Switch
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { Amplify } from 'aws-amplify';
import awsconfig from '@/amplifyconfiguration.json';
import { getPatient } from '@/graphql/queries';
import { fetchAuthSession } from 'aws-amplify/auth';
import { mypageTabComponentProps, TabComponent } from '@/components/Elements/TabComponent/TabComponent';
import { MedicalHistory } from '@/features/mypage/components/MedicalHistory';
import { AsYouType } from 'libphonenumber-js';
import { v4 as uuidv4 } from 'uuid';
import { approval } from '@/features/mynumber/constants/Api';
import { OSIdentification } from '@/features/mynumber/routes';
import { isLineWebBrowser } from '@/features/mynumber/routes';
import { ReservationUrls } from '@/features/reservation/constants/urls';
import { CookieStorage } from 'aws-amplify/utils';
import { useNavigate } from 'react-router';
import { listFacilityPatients } from '@/graphql/queries';
import { updatePatient } from '@/graphql/mutations';
import { UnsupportedBrowserDialog } from '@/components/Elements/Dialog/UnsupportedBrowserDialog';
import { notMatchCustomErrorMessage } from '@/lib/sentryClient';
import * as Sentry from '@sentry/react';
import { executePost } from '@/client/rest';
import { GetPatientQuery, ListFacilityPatientsQuery, UpdatePatientMutation } from '@/API';
import { executeQuery } from '@/client/graphql';

Amplify.configure(awsconfig);

export function MyPage(props: any) {
  const [reservationList, setReservationList] = useState<any[]>([]); // 予約一覧用のステート
  const [patientInfo, setPatientInfo] = useState<any>(null); // 患者情報用のステート
  const [backdropOpen, setBackdropOpen] = useState<boolean>(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showAllReservations, setShowAllReservations] = useState<boolean>(false);
  const [myNumberModalOpen, setMyNumberModalOpen] = useState<boolean>(false);
  const [unsupportedBrowserDialog, setUnsupportedBrowserDialog] = useState<boolean>(false);
  const [isIos, setIsIos] = useState<boolean>(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false); // 通知設定表示用のステート
  const navigate = useNavigate();

  // 今日以降の日付のみをフィルタリングする関数
  const filterFutureReservations = (reservations: any[]) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return reservations.filter((reservation) => {
      if (reservation.date == null) {
        return true; // null dates are included
      }
      const reservationDate = new Date(
        parseInt(reservation.date.substring(0, 4), 10),
        parseInt(reservation.date.substring(4, 6), 10) - 1,
        parseInt(reservation.date.substring(6, 8), 10)
      );
      return reservationDate >= today;
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const session = await fetchAuthSession();
        const cognitoid = session.userSub;

        const result = await executeQuery<GetPatientQuery>(
          getPatient,
          {
            id: session.userSub as string
          },
          'userPool'
        );

        if (result.getPatient) {
          setPatientInfo(result.getPatient);
          // 患者情報が存在する場合のみ、予約情報を取得
          const url = `${import.meta.env.VITE_RESERVE_API_URL}/api/reservation/getreservationlist/${cognitoid}/`;
          const res = await fetch(encodeURI(url), {
            mode: 'cors'
          });
          const menus = await res.json();
          const processedMenus = filterFutureReservations(menus);
          setReservationList(processedMenus);

          // ユーザがLINE登録している場合に通知設定を表示
          // FacilityPatientsテーブルのlineUserIdの値があれば、LINE登録している施設が1つでもあると判断
          const facilityPatientsResult = await executeQuery<ListFacilityPatientsQuery>(
            listFacilityPatients,
            {
              filter: { patientId: { eq: cognitoid } }
            },
            'userPool'
          );

          if (facilityPatientsResult.listFacilityPatients?.items) {
            // `FacilityPatients` テーブルから得たアイテムをループしてlineUserIdがあるかチェック
            const hasLineUserId = facilityPatientsResult.listFacilityPatients.items.some(
              (item: any) => item.lineUserId && item.lineUserId.trim() !== ''
            );

            // 1つでもlineUserIdがあれば通知設定を表示
            if (hasLineUserId) {
              setShowNotificationSettings(true);
            }
          }
        } else {
          // 患者情報が存在しない場合、ステップ6に遷移
          props.setCurrentState((prevState: any) => ({
            ...prevState,
            isFromMypage: true
          }));
          props.setActiveStep(6);
        }
      } catch (error) {
        if (error instanceof Error && notMatchCustomErrorMessage(error.name)) {
          Sentry.captureException(error);
        }

        console.error('Failed to fetch data:', error);
        // エラーが発生した場合もステップ6に遷移
        props.setActiveStep(6);
      } finally {
        setIsLoading(false);
        setIsIos(OSIdentification());
        setBackdropOpen(false);
      }
    };

    fetchData();
  }, [props]);

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
  };

  const handleCancelReservation = async (
    reserveid: number,
    examinationMenuId: string,
    reservationDate: string,
    reservationStartime: string
  ) => {
    setBackdropOpen(true);
    try {
      const session = await fetchAuthSession();
      const cognitoid = session.userSub as string;

      const res = (await executePost({
        apiName: 'medirecoWebApi',
        path: '/cancel-reservation',
        options: {
          body: {
            cognitoId: cognitoid,
            reserveId: reserveid,
            outpatientId: examinationMenuId,
            reservateionDate: reservationDate,
            startTime: reservationStartime
          }
        }
      })) as any;

      setCancelDialogOpen(false); // キャンセルダイアログを閉じる
      // キャンセル完了画面に遷移
      props.setActiveStep(1);
    } catch (error: any) {
      if (error instanceof Error && notMatchCustomErrorMessage(error.name)) {
        Sentry.captureException(error);
      }

      props.toErrorPage('ご予約のキャンセルエラーが発生しました。', 'ご予約キャンセルエラー');
      console.error('Failed to cancel reservation:', error);
    } finally {
      setBackdropOpen(false);
    }
  };

  const handleGoToNewReservation = () => {
    const redirectUrl = ReservationUrls.Reservation;
    const encodedRedirectUrl = encodeURIComponent(redirectUrl);
    navigate(`/patient/facilities?redirect=${encodedRedirectUrl}`, { replace: true });
  };

  const getFormattedDate = (date: string) => {
    const year = date.substring(0, 4);
    const month = date.substring(4, 6);
    const day = date.substring(6, 8);
    return `${year}/${month}/${day}`;
  };

  const getFormattedTime = (time: string) => {
    const hour = time.substring(0, 2);
    const minute = time.substring(2, 4);
    return `${hour}:${minute}`;
  };

  const getDayOfWeek = (date: string) => {
    const week = ['日', '月', '火', '水', '木', '金', '土'];
    const year = parseInt(date.substring(0, 4), 10);
    const month = parseInt(date.substring(4, 6), 10) - 1;
    const day = parseInt(date.substring(6, 8), 10);
    const dateObj = new Date(year, month, day);
    return week[dateObj.getDay()];
  };

  const getFormattedDateTime = (date: string, time: string) => {
    const year = date.substring(0, 4);
    const month = date.substring(4, 6);
    const day = date.substring(6, 8);
    const hour = time.substring(0, 2);
    const minute = time.substring(2, 4);
    return `${year}年 ${month}月 ${day}日 (${getDayOfWeek(date)}) ${hour}:${minute.padStart(2, '0')}`;
  };

  const renderReservationInfo = (reservation: any) => (
    <Box sx={{ textAlign: 'left', width: '100%' }}>
      <Typography className="font-bold">
        {reservation.date !== null && (
          <>
            <span style={{ fontSize: '12px' }}>{getFormattedDate(reservation.date)}　</span>
            <span style={{ fontSize: '12px' }}>{getFormattedTime(reservation.startTime)}　</span>
          </>
        )}
        <span
          style={{
            fontSize: '12px',
            backgroundColor: Number(reservation.paymentFlag) === 0 ? '#fc0356' : 'lightgray', // 背景色を条件で変更
            color: Number(reservation.paymentFlag) === 0 ? '#FFFFFF' : '#000000', // 文字色を条件で変更
            padding: '0.2em 0.6em',
            borderRadius: '0.25rem',
            display: 'inline-block',
            marginLeft: '0.5em'
          }}
        >
          {Number(reservation.paymentFlag) === 0 ? '未会計' : 'お会計済み'}
        </span>
        <Box component="span" sx={{ display: 'block', mt: 1 }} />
        <span
          style={{
            fontWeight: 'normal',
            marginRight: '10px',
            fontSize: '12px'
          }}
        >
          {reservation.facilityName}
        </span>
        <br />
        <span>
          {reservation.outpatientName}
          {/* {reservation.visitCount}回目 */}
        </span>
      </Typography>
    </Box>
  );

  const handleShowAll = () => {
    setShowAllReservations(true);
  };

  if (isLoading) {
    return (
      <Backdrop sx={{ color: '#fff', backgroundColor: '#0003', zIndex: (theme) => theme.zIndex.modal + 1 }} open={true}>
        <CircularProgress color={'inherit'} />
      </Backdrop>
    );
  }

  // 患者情報がない場合は何も表示しない
  if (!patientInfo) {
    return null;
  }

  const handleGoToReservationDateTime = (reservation: any, isChange: boolean) => {
    const newState = {
      reserveid: reservation.reserveId,
      examinationMenuId: reservation.outpatientId,
      examinationMenuName: reservation.outpatientName,
      reservationDate: reservation.date,
      reservationStartime: reservation.startTime,
      isChange: isChange,
      BeforeChangeOutpatientId: props.currentState.examinationMenuId,
      BeforeChangeReservateionDate: props.currentState.reservationDate,
      BeforeChangeStartTime: props.currentState.reservationStartime,
      facilityId: reservation.facilityId
    };

    props.setCurrentState(newState);
    props.setActiveStep(3);
  };

  const handleGoToCancelMenue = ({
    reserveid,
    examinationMenuId,
    examinationMenuName,
    reservationDate,
    reservationStartime
  }: {
    reserveid: number;
    examinationMenuId: string;
    examinationMenuName: string;
    reservationDate: string | null;
    reservationStartime: string | null;
  }) => {
    // 必要なデータを設定
    props.setCurrentState({
      ...props.currentState,
      reserveid,
      examinationMenuId,
      examinationMenuName,
      reservationDate,
      reservationStartime
    });
    setCancelDialogOpen(true); // ダイアログを開く
  };

  interface ReservationButtonProps {
    label: string;
    onClick: () => void;
    variant: 'contained' | 'outlined';
    color: 'primary' | 'error';
    fullWidth?: boolean;
  }

  const ReservationButton: React.FC<ReservationButtonProps> = ({
    label,
    onClick,
    variant,
    color,
    fullWidth = false
  }) => (
    <Button
      className={variant === 'contained' ? 'bg-primary flex relative rounded' : ''}
      variant={variant}
      color={color}
      sx={{
        width: fullWidth ? '100%' : '48%',
        height: '36px',
        color: variant === 'contained' ? '#FFFFFF' : undefined,
        textAlign: 'center',
        backgroundColor: variant === 'contained' ? '#1976d2' : undefined,
        borderColor: color === 'error' ? '#d32f2f' : undefined
      }}
      onClick={onClick}
    >
      {label}
    </Button>
  );

  const renderReservationButtons = (reservation: any) => {
    const isNewReservation = reservation.date == null || reservation.startTime == null;

    if (isNewReservation) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <ReservationButton
            label="次回予約受付"
            onClick={() => handleGoToReservationDateTime(reservation, true)}
            variant="contained"
            color="primary"
          />
        </Box>
      );
    } else {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <ReservationButton
            label="予約日時変更"
            onClick={() => handleGoToReservationDateTime(reservation, true)}
            variant="contained"
            color="primary"
          />
          <ReservationButton
            label="キャンセル"
            onClick={() =>
              handleGoToCancelMenue({
                reserveid: reservation.reserveId,
                examinationMenuId: reservation.outpatientId,
                examinationMenuName: reservation.outpatientName,
                reservationDate: reservation.date,
                reservationStartime: reservation.startTime
              })
            }
            variant="outlined"
            color="error"
          />
        </Box>
      );
    }
  };

  const PatientInfoForm = () => {
    const b = patientInfo.dateOfBirth?.split('-');

    return (
      <Grid>
        <Grid container direction={'column'} mx={1} my={0.5} spacing={2}>
          <Grid item>
            <Typography fontSize={14} className="font-bold">
              氏名
            </Typography>
            <Typography>
              {`${patientInfo.familyName}　${patientInfo.firstName}`}（
              {`${patientInfo.familyNameKana}　${patientInfo.firstNameKana}`}）
            </Typography>
          </Grid>
          {patientInfo.dateOfBirth && (
            <Grid item>
              <Typography fontSize={14} className="font-bold">
                生年月日
              </Typography>
              <Typography>
                {b[0]}年 {b[1].replace(/^0/, '')}月 {b[2].replace(/^0/, '')}日
              </Typography>
            </Grid>
          )}
          <Grid item>
            <Typography fontSize={14} className="font-bold">
              電話番号
            </Typography>
            <Typography>{new AsYouType('JP').input(patientInfo.phoneNumber)}</Typography>
          </Grid>
          <Grid item>
            <Typography fontSize={14} className="font-bold">
              メールアドレス
            </Typography>
            <Typography>{patientInfo.email}</Typography>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const NotificationSettingForm = () => {
    // patientInfo.isLineNotificationEnabledがない場合はtrueにする（既存ユーザー考慮）
    if (patientInfo.isLineNotificationEnabled === null) {
      try {
        executeQuery<UpdatePatientMutation>(
          updatePatient,
          {
            input: {
              id: patientInfo.id,
              isLineNotificationEnabled: true
            }
          },
          'userPool'
        );
      } catch (error) {
        if (error instanceof Error && notMatchCustomErrorMessage(error.name)) {
          Sentry.captureException(error);
        }

        console.error('Failed to update patient isLineNotificationEnabled:', error);
      }
      patientInfo.isLineNotificationEnabled = true;
    }

    const [isLineNotificationEnabled, setIsLineNotificationEnabled] = useState<boolean>(
      patientInfo.isLineNotificationEnabled
    );

    const handleSwitchChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.checked; // 新しい状態（ON/OFF）

      setIsLineNotificationEnabled(newValue);

      try {
        const patientId = patientInfo.id;

        await executeQuery<UpdatePatientMutation>(
          updatePatient,
          {
            input: {
              id: patientId,
              isLineNotificationEnabled: newValue
            }
          },
          'userPool'
        );
      } catch (error) {
        if (error instanceof Error && notMatchCustomErrorMessage(error.name)) {
          Sentry.captureException(error);
        }

        console.error('Failed to update patient isLineNotificationEnabled:', error);
        // エラーの場合、スイッチを元に戻す
        setIsLineNotificationEnabled(!newValue);
      }
    };

    return (
      <Grid>
        <Grid item sx={{ mt: 3 }}>
          <Typography fontSize={16} className="font-bold text-primary">
            通知設定
          </Typography>
        </Grid>
        <Grid item>
          <Grid container direction="row" alignItems="center" justifyContent="space-between">
            <Grid item sx={{ mt: 1, ml: 2 }}>
              <Typography fontSize={16} className="font-bold">
                LINE通知
              </Typography>
              <Typography fontSize={14}>施設公式LINEの通知を受け取る</Typography>
            </Grid>
            <Grid item>
              <Switch checked={isLineNotificationEnabled} onChange={handleSwitchChange} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const RegisterButton = (props: { label: string; onClick: () => void; bgColor: string }) => {
    return (
      <div style={{ backgroundColor: props.bgColor, marginBottom: '1rem', borderRadius: '4px' }}>
        <Button
          onClick={props.onClick}
          fullWidth
          sx={{
            height: '48px'
          }}
          color="inherit"
        >
          <Typography className="font-bold" color={'white'}>
            {props.label}
          </Typography>
        </Button>
      </div>
    );
  };

  // LINE認証関連、今は使っておりませんが、実装する際に利用するため残しております。
  const lineAuthHref = (authorize: boolean) => {
    const state = uuidv4();
    sessionStorage.setItem('line_auth_state', state);
    return `https://access.line.me/oauth2/v2.1/authorize?response_type=code&client_id=**********&redirect_uri=${encodeURIComponent(
      `${window.location.origin}/patient/mypage/lineauth?type=${authorize ? 'authorize' : 'deauthorize'}`
    )}&state=${state}&scope=openid`;
  };

  const DeleteUserButton = () => {
    const redirectDeleteUserForm = () => {
      // ユーザー削除フォームに遷移する
      const deleteUserFormUrl = import.meta.env.VITE_DELETE_USER_FORM_URL;
      window.location.href = deleteUserFormUrl || '';
    };

    return (
      <Button
        className=""
        variant="outlined"
        color="error"
        sx={{
          width: '100%',
          height: '36px',
          textAlign: 'center',
          borderColor: '#d32f2f',
          fontWeight: 'bold'
        }}
        onClick={redirectDeleteUserForm}
      >
        アカウントを削除する
      </Button>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container direction={'row'} spacing={2}>
        <Grid item xs={0} md={3} sm={2} />
        <Grid item xs={12} md={6} sm={8}>
          <Grid container direction={'column'} spacing={2}>
            <Grid item xs={12} sx={{ textAlign: 'left' }}>
              <Grid
                container
                direction={'row'}
                justifyContent={'space-between'}
                alignItems={'center'}
                className="text-primary"
              >
                <Typography fontSize={16} className="font-bold text-primary">
                  お客様情報
                </Typography>

                <Button
                  startIcon={<EditIcon sx={{ marginRight: '2px' }} />}
                  variant="outlined"
                  color="inherit"
                  sx={{ paddingX: '8px', paddingY: '4px' }}
                  onClick={() => {
                    props.setCurrentState({ ...props.currentState, patientInformation: patientInfo });
                    props.setActiveStep(8);
                  }}
                >
                  <Typography fontSize={14} className="font-bold text-primary" my={'auto'}>
                    編集
                  </Typography>
                </Button>
              </Grid>
              {patientInfo && <PatientInfoForm />}
              {showNotificationSettings && <NotificationSettingForm />}
              <Grid container direction={'column'} my={3}>
                <RegisterButton
                  label="マイナンバーカード本人確認"
                  onClick={() => {
                    if (isLineWebBrowser()) {
                      setUnsupportedBrowserDialog(true);
                    } else {
                      setMyNumberModalOpen(true);
                    }
                  }}
                  bgColor="#0017C1"
                />
              </Grid>

              <TabComponent
                {...mypageTabComponentProps}
                tabs={[
                  {
                    label: '予約一覧',
                    component: (
                      <Grid container spacing={0}>
                        <Box
                          display="flex"
                          flexDirection="column"
                          gap={0.5}
                          sx={{
                            overflow: 'hidden',
                            width: '100%'
                          }}
                        >
                          {(showAllReservations ? reservationList : reservationList.slice(0, 3)).map(
                            (reservation, index, array) => (
                              <Box
                                key={index}
                                sx={{
                                  p: 2,
                                  backgroundColor: 'white'
                                }}
                              >
                                <Box display="flex" flexDirection="column" alignItems="flex-start">
                                  {renderReservationInfo(reservation)}
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'row',
                                      justifyContent: 'space-between',
                                      width: '100%',
                                      mt: 2
                                    }}
                                  >
                                    {renderReservationButtons(reservation)}
                                  </Box>
                                </Box>
                              </Box>
                            )
                          )}
                          {!showAllReservations && reservationList.length > 3 && (
                            <Button
                              fullWidth
                              variant="outlined"
                              className="text-primary text-center font-bold text-sm"
                              onClick={handleShowAll}
                              sx={{
                                backgroundColor: 'white',
                                height: '40px',
                                minHeight: '40px',
                                border: 'none'
                                // borderRadius: 0
                              }}
                            >
                              もっと見る
                            </Button>
                          )}
                        </Box>
                        <Grid item xs={12} mt={4}>
                          <Button
                            className="bg-primary flex relative rounded"
                            variant="contained"
                            color="primary"
                            sx={{
                              width: '100%',
                              height: '38px',
                              color: '#FFFFFF',
                              textAlign: 'center',
                              margin: 'auto',
                              mt: 2,
                              mb: 2,
                              backgroundColor: '#1976d2',
                              padding: '20px 20px'
                            }}
                            onClick={handleGoToNewReservation}
                          >
                            新規予約こちらから
                          </Button>
                        </Grid>
                      </Grid>
                    )
                  },
                  {
                    label: '診療履歴',
                    component: <MedicalHistory />
                  }
                ]}
              />
              <DeleteUserButton />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={0} md={3} sm={2} />
      </Grid>
      <Backdrop
        sx={{ color: '#fff', backgroundColor: '#0003', zIndex: (theme) => theme.zIndex.modal + 1 }}
        open={backdropOpen}
      >
        <CircularProgress color={'inherit'} />
      </Backdrop>

      {/* マイナンバー確認ダイアログ */}
      <Dialog
        fullWidth
        open={myNumberModalOpen}
        onClose={() => {
          setMyNumberModalOpen(false);
        }}
        aria-labelledby="mynumber-dialog-title"
        aria-describedby="mynumber-dialog-description"
      >
        <IconButton
          aria-label="close"
          onClick={() => {
            setMyNumberModalOpen(false);
          }}
          sx={(theme) => ({
            position: 'relative',
            padding: 0,
            marginLeft: 'auto',
            paddingRight: '8px',
            paddingTop: '8px',
            color: theme.palette.grey[500]
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle id="mynumber-dialog-title" className="font-bold" sx={{ fontSize: '16px', textAlign: 'center' }}>
          デジタル認証アプリをダウンロード
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', backgroundColor: '#FFF', paddingBottom: '32px' }}>
          <DialogContentText fontSize={'14px'} color={'#000'} sx={{ marginBottom: '24px' }}>
            この先の手続きを進めるためには、
            <br />
            デジタル庁が提供する
            <br />
            デジタル認証アプリが必要です。
          </DialogContentText>
          <div className="flex flex-row justify-center items-between gap-4">
            <Grid item>
              <div className="h-full flex flex-col justify-start items-center gap-2">
                <div className="flex justify-center items-center w-[52px] h-[52px] rounded-xl border border-2 border-gray">
                  <img className="w-full h-full p-1" src="/images/mynumber/App-Icon_Blue.svg" alt="" />
                </div>
                <Typography className="font-bold" fontSize={13} whiteSpace={'nowrap'}>
                  デジタル認証アプリ
                </Typography>
              </div>
            </Grid>
            <Grid item>
              <div className="flex flex-col gap-1">
                {isIos === true ? (
                  <a href="https://apps.apple.com/jp/app/id6454900894" target="_blank" rel="noopener noreferrer">
                    <img src="/images/mynumber/DownloadOnTheAppStore.svg" alt="" />
                  </a>
                ) : (
                  <a
                    href="https://play.google.com/store/apps/details?id=jp.go.digital.auth_and_sign&hl=ja"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src="/images/mynumber/GetItOnGooglePlay.svg" alt="" />
                  </a>
                )}
              </div>
            </Grid>
          </div>
          <DialogActions>
            <Grid container direction={'column'} alignItems={'center'}>
              <button
                className="max-w-[360px] mt-6"
                onClick={async () => {
                  const cookie = new CookieStorage();
                  await cookie.setItem('mynumber_redirect_path', ReservationUrls.Mypage);

                  // mynumber関連の施設IDの仕様が未確定のため、現時点ではmynumberに関連する施設IDのみを
                  // セッションストレージとcookieに保存する仕様としている
                  // mynumber周り以外は、sessionStorageは廃止しているため、この処理は暫定的なもの
                  // 施設を選ぶUIがないため、直接施設ID:0001（リジェ）を入れている
                  sessionStorage.setItem('facility_id', '0001');
                  await cookie.setItem('facility_id', '0001');
                  approval();
                }}
              >
                <img src="/images/mynumber/Btn_Dark_Filled_Default.png" alt="" />
              </button>
            </Grid>
          </DialogActions>
        </DialogContent>
      </Dialog>

      {/* デジタル認証アプリがLINEの内部ブラウザから開かれた場合のダイアログ */}
      <UnsupportedBrowserDialog
        open={unsupportedBrowserDialog}
        onClose={() => setUnsupportedBrowserDialog(false)}
        isIos={isIos}
      />

      {/* キャンセル確認ダイアログ */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCancelDialogClose}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
      >
        <DialogTitle id="cancel-dialog-title" sx={{ textAlign: 'center' }} fontWeight={'bold'}>
          予約をキャンセルして
          <br />
          よろしいですか？
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" marginLeft={'8px'} marginTop={'8px'} fontWeight={'bold'}>
            診療メニュー
          </Typography>
          <Typography variant="body2" marginLeft={'8px'}>
            {props.currentState.examinationMenuName}
          </Typography>
          <Typography variant="subtitle2" marginLeft={'8px'} marginTop={'8px'} fontWeight={'bold'}>
            お名前
          </Typography>
          <Typography variant="body2" marginLeft={'8px'}>
            {patientInfo.familyName} {patientInfo.firstName} 様
          </Typography>
          <Typography variant="subtitle2" marginLeft={'8px'} marginTop={'8px'} fontWeight={'bold'}>
            ご予約日時
          </Typography>
          <Typography variant="body2" marginLeft={'8px'}>
            {props.currentState.reservationDate && props.currentState.reservationStartime
              ? getFormattedDateTime(props.currentState.reservationDate, props.currentState.reservationStartime)
              : 'XXXX年XX月XX日(X)XX:XX'}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Paper
              style={{
                overflowY: 'scroll',
                maxHeight: '300px',
                padding: '10px',
                marginTop: '20px',
                marginBottom: '20px',
                border: '1px solid lightgray',
                borderRadius: '4px',
                maxWidth: '90%',
                width: '100%'
              }}
            >
              <Typography className="text-primary text-center font-bold text-sm" mb={2}>
                ご注意事項
              </Typography>
              <Typography>
                当院の診療は全て予約制です。
                <br />
                予約時間からの診療を保証するものではなく、来院時間の目安となります。
                <br />
                診療開始が遅れる場合には、患者様にご連絡いたしますので、指示に従って受診ください。
                <br />
                また、当日の予約時間の5分前までにオンライン事前問診されていない場合や、受付時間までに自動受付をしないと予約は自動キャンセルとなります。
                <br />
                予約が自動的にキャンセルされた場合、次回以降の予約可能時間に制限がかかりますのでご注意ください。
              </Typography>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', alignItems: 'center', width: '100%' }}>
          <Button
            variant="outlined"
            color="error"
            sx={{
              width: '200px', // 幅を200pxに設定
              height: '36px',
              color: '#ddddd',
              textAlign: 'center',
              borderColor: '#d32f2f',
              margin: '0px 0px 8px',
              padding: '5px 15px'
            }}
            onClick={() =>
              handleCancelReservation(
                props.currentState.reserveid,
                props.currentState.examinationMenuId,
                props.currentState.reservationDate,
                props.currentState.reservationStartime
              )
            }
          >
            キャンセルする
          </Button>
          <Button
            className="bg-primary flex relative rounded"
            variant="contained"
            color="primary"
            sx={{
              width: '200px', // 幅を200pxに設定
              height: '36px',
              color: '#FFFFFF',
              textAlign: 'center',
              backgroundColor: '#1976d2',
              margin: '0px 8px 8px 0px',
              padding: '6px 16px'
            }}
            onClick={handleCancelDialogClose}
          >
            キャンセルをやめる
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MyPage;
